import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../shared/context/AuthContext';
import { useTheme } from '../shared/context/ThemeContext';
import { NavIcon, EyeIcon } from './components/AdminIcons';
import ResultsSection        from './sections/Results/ResultsSection';
import CandidatesSection     from './sections/Candidates/CandidatesSection';
import AddCandidateSection   from './sections/AddCandidate/AddCandidateSection';
import BulkImportSection     from './sections/BulkImport/BulkImportSection';
import QuestionsSection      from './sections/Questions/QuestionsSection';
import TestConfigSection     from './sections/TestConfig/TestConfigSection';
import EmailSettingsSection  from './sections/EmailSettings/EmailSettingsSection';
import AdminsSection         from './sections/Admins/AdminsSection';
import RolesSection          from './sections/Roles/RolesSection';
import CompanySettingsSection from './sections/CompanySettings/CompanySettingsSection';
import './AdminPage.css';

const ALL_TABS = [
  { key: 'results',     label: 'Results',        module: 'results',     icon: <NavIcon d="M3 3v18h18M7 16l4-4 4 4 4-6" /> },
  { key: 'candidates',  label: 'Candidates',     module: 'candidates',  icon: <NavIcon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" d2="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /> },
  { key: 'create',      label: 'Add Candidate',  module: 'candidates',  icon: <NavIcon d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM19 8v6M22 11h-6" /> },
  { key: 'bulk',        label: 'Bulk Import',    module: 'candidates',  icon: <NavIcon d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" /> },
  { key: 'questions',   label: 'Questions',      module: 'questions',   icon: <NavIcon d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" d2="M14 2v6h6M16 13H8M16 17H8M10 9H8" /> },
  { key: 'testconfig',  label: 'Test Config',    module: 'testconfig',  icon: <NavIcon d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" d2="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /> },
  { key: 'emailconfig', label: 'Email Settings', module: 'emailconfig', icon: <NavIcon d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" d2="M22 6l-10 7L2 6" /> },
  { key: 'admins',      label: 'Admins',            module: 'admins',      masterOnly: true, icon: <NavIcon d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" d2="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8" /> },
  { key: 'roles',       label: 'Roles',             module: 'roles',       masterOnly: true, icon: <NavIcon d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /> },
  { key: 'company',     label: 'Company Settings',  module: 'company',     masterOnly: true, icon: <NavIcon d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" d2="M9 22V12h6v10" /> },
];

export default function AdminPage() {
  const { user, logout, changePassword } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const can = useCallback((module, action) => {
    if (user?.role === 'master_admin') return true;
    return user?.permissions?.[module]?.[action] === true;
  }, [user]);

  const isMaster = user?.role === 'master_admin';

  const TABS = ALL_TABS.filter(t => {
    if (t.masterOnly) return isMaster;
    return can(t.module, 'view');
  });

  const [tab, setTab] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && TABS.find(t => t.key === hash)) return hash;
    return TABS[0]?.key || 'results';
  });
  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && TABS.find(t => t.key === hash)) setTab(hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [TABS]);

  const [toasts, setToasts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Change password modal
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', newPass: '', confirm: '' });
  const [pwdShow, setPwdShow] = useState({ current: false, newPass: false, confirm: false });
  const [pwdError, setPwdError] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  const notify = useCallback((msg, isErr = false) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, msg, isErr }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4500);
  }, []);
  const dismissToast = id => setToasts(t => t.filter(x => x.id !== id));

  const handleChangePwd = async e => {
    e.preventDefault();
    setPwdError('');
    if (pwdForm.newPass.length < 8) return setPwdError('New password must be at least 8 characters.');
    if (pwdForm.newPass !== pwdForm.confirm) return setPwdError('Passwords do not match.');
    if (pwdForm.newPass === pwdForm.current) return setPwdError('New password must differ from current password.');
    setPwdLoading(true);
    try {
      await changePassword(pwdForm.current, pwdForm.newPass);
      notify('Password changed successfully');
      setShowChangePwd(false);
      setPwdForm({ current: '', newPass: '', confirm: '' });
    } catch (err) {
      setPwdError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setPwdLoading(false);
    }
  };

  const sectionProps = { notify, can };

  return (
    <div className={`admin-layout${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={()=>setSidebarOpen(false)}/>}

      {/* ══════════════ HEADER ══════════════ */}
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-brand">
            <div className="sidebar__brand-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <rect x="2"  y="2"  width="9" height="9" rx="2"/>
                <rect x="13" y="2"  width="9" height="9" rx="2"/>
                <rect x="2"  y="13" width="9" height="9" rx="2"/>
                <rect x="13" y="13" width="9" height="9" rx="2"/>
              </svg>
            </div>
            <div className="admin-brand-text">
              <span className="brand-name">AdminPanel</span>
              <span className="brand-sub">Management Console</span>
            </div>
          </div>
          <button
            className="hdr-sidebar-toggle"
            onClick={() => { if (window.innerWidth <= 768) setSidebarOpen(o=>!o); else setSidebarCollapsed(c=>!c); }}
            aria-label="Toggle sidebar"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6"  x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="admin-header-right">
          <button className="hdr-icon-btn" title="Notifications">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
          </button>
          <button className="hdr-icon-btn" title="Messages">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
          <button className="hdr-icon-btn hdr-theme-btn" onClick={toggleTheme} title={theme==='dark'?'Switch to Light':'Switch to Dark'}>
            {theme === 'dark'
              ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            }
          </button>
          <div className="hdr-divider"/>
          <div className="hdr-user">
            <div className="hdr-user-info">
              <div className="hdr-user-name">{user?.full_name || user?.username}</div>
              <div className="hdr-user-role">
                {isMaster ? 'Master Admin' : user?.role === 'evaluator' ? 'Evaluator' : 'Admin'}
              </div>
            </div>
            <div className="hdr-user-avatar">
              {(user?.full_name || user?.username || '?')[0].toUpperCase()}
            </div>
          </div>
        </div>
      </header>

      {/* ══════════════ SIDEBAR ══════════════ */}
      <aside className={`sidebar${sidebarOpen?' sidebar--open':''}`}>
        <nav className="sidebar__nav">
          {TABS.map(t => (
            <button key={t.key} className={`sidebar__nav-item${tab===t.key?' active':''}`}
              data-label={t.label}
              onClick={() => { window.location.hash = t.key; setSidebarOpen(false); }}>
              {t.icon}
              <span className="sidebar__nav-label">{t.label}</span>
            </button>
          ))}
        </nav>

        {/* Zone 3 — change password + sign out */}
        <div className="sidebar__bottom">
          <button
            className="sidebar__bottom-link"
            data-label="Change Password"
            onClick={() => { setShowChangePwd(true); setPwdError(''); setPwdForm({ current:'',newPass:'',confirm:'' }); setSidebarOpen(false); }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span className="sidebar__text">Change Password</span>
          </button>
          <button
            className="sidebar__bottom-link sidebar__bottom-link--logout"
            data-label="Sign Out"
            onClick={logout}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span className="sidebar__text">Sign Out</span>
          </button>
        </div>

      </aside>

      {/* ══════════════ MAIN ══════════════ */}
      <main className="admin-main">
        <div className="admin-topbar">
          <h1 className="admin-page-title">{TABS.find(t=>t.key===tab)?.label}</h1>
        </div>

        {tab === 'results'     && <ResultsSection        {...sectionProps} />}
        {tab === 'candidates'  && <CandidatesSection     {...sectionProps} />}
        {tab === 'create'      && <AddCandidateSection   {...sectionProps} />}
        {tab === 'bulk'        && <BulkImportSection      {...sectionProps} />}
        {tab === 'questions'   && <QuestionsSection       {...sectionProps} />}
        {tab === 'testconfig'  && <TestConfigSection      {...sectionProps} />}
        {tab === 'emailconfig' && <EmailSettingsSection   {...sectionProps} />}
        {tab === 'admins'      && <AdminsSection          {...sectionProps} />}
        {tab === 'roles'       && <RolesSection           {...sectionProps} />}
        {tab === 'company'     && <CompanySettingsSection {...sectionProps} />}
      </main>

      {/* ════════ TOAST STACK ════════ */}
      <div className="toast-stack">
        {toasts.map(t => (
          <div key={t.id} className={`toast-item ${t.isErr ? 'toast-error' : 'toast-success'}`}>
            <div className="toast-icon-wrap">
              {t.isErr
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              }
            </div>
            <span className="toast-msg">{t.msg}</span>
            <button className="toast-close" onClick={() => dismissToast(t.id)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        ))}
      </div>

      {/* ════════ CHANGE PASSWORD MODAL ════════ */}
      {showChangePwd && (
        <div className="ev-overlay" onClick={()=>setShowChangePwd(false)}>
          <div className="ev-modal" style={{maxWidth:'420px'}} onClick={e=>e.stopPropagation()}>
            <div className="ev-modal-header">
              <div className="ev-modal-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color:'var(--accent2)'}}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Change Password
              </div>
              <button className="ev-close-btn" onClick={()=>setShowChangePwd(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {pwdError && (
              <div className="login-error" style={{margin:'0 1.5rem 0'}}>
                <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/></svg>
                {pwdError}
              </div>
            )}
            <form onSubmit={handleChangePwd} style={{padding:'1.25rem 1.5rem',display:'flex',flexDirection:'column',gap:'1rem'}}>
              {[
                { label: 'Current Password',     name: 'current',  ph: 'Your current password' },
                { label: 'New Password',          name: 'newPass',  ph: 'At least 8 characters' },
                { label: 'Confirm New Password',  name: 'confirm',  ph: 'Re-enter new password' },
              ].map(({ label, name, ph }) => (
                <div key={name} className="input-group" style={{margin:0}}>
                  <label>{label}</label>
                  <div style={{position:'relative',display:'flex',alignItems:'center'}}>
                    <input
                      type={pwdShow[name] ? 'text' : 'password'}
                      className="input-field"
                      style={{paddingRight:'2.6rem'}}
                      placeholder={ph}
                      value={pwdForm[name]}
                      onChange={e=>setPwdForm(f=>({...f,[name]:e.target.value}))}
                      required
                    />
                    <button type="button" style={{position:'absolute',right:'0.65rem',background:'none',border:'none',cursor:'pointer',color:'var(--muted)',padding:0,display:'flex',alignItems:'center'}}
                      onClick={()=>setPwdShow(s=>({...s,[name]:!s[name]}))}>
                      <EyeIcon visible={pwdShow[name]}/>
                    </button>
                  </div>
                </div>
              ))}
              <div className="ev-modal-footer" style={{padding:'0.5rem 0 0',justifyContent:'flex-end'}}>
                <button type="button" className="btn btn-outline btn-sm" onClick={()=>setShowChangePwd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={pwdLoading}>
                  {pwdLoading ? <><span className="btn-spinner"/>Saving…</> : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
